import React, { useState, useEffect, memo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Menu,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Send as SendIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import userManagementService from '../../../services/userManagementService';
import { UserInfo, UserRole, UserStatus, UserStats, UserInvitation } from '../../../types/userManagement';
import type { ComprehensiveSettings } from '../../../types/settings';
import { useOrganization, type StoreInfo } from '../../../hooks/useOrganization';
import type { TenantContext } from '../../../types/multiTenant';
import type { UnknownObject, ReactChangeEvent, ReactClickEvent } from '../../../types/common.types';

// XSS防止のための入力サニタイゼーション関数
const sanitizeInput = (input: string): string => {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * ユーザー管理タブコンポーネント - 組織階層対応版
 * @description スタッフアカウント管理・権限設定・招待機能・組織階層管理
 * @features CRUD操作、バルク編集、招待システム、権限管理、テナント・店舗管理
 */

interface UserManagementTabProps {
  settings?: ComprehensiveSettings;
  updateSettings?: (section: keyof ComprehensiveSettings, updates: UnknownObject) => Promise<void>;
  // 🆕 組織階層管理用プロパティ (Issue #285)
  tenantContext?: TenantContext;
  organizationView?: boolean;
}

interface UserEditorDialogProps {
  open: boolean;
  user: UserInfo | null;
  onClose: () => void;
  onSave: (userData: Partial<UserInfo>) => void;
}

interface InvitationDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (email: string, role: UserRole, message?: string) => void;
  // 🆕 Organization support (Issue #285)
  organizationView?: boolean;
  availableStores?: StoreInfo[];
  tenantContext?: TenantContext;
}

const UserManagementTab: React.FC<UserManagementTabProps> = ({
  settings: _settings,
  updateSettings: _updateSettings,
  tenantContext,
  organizationView = false,
}) => {
  // 🆕 Organization Hook Integration (Issue #285)
  const organization = useOrganization();

  // State management (既存完全保持)
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [, setInvitations] = useState<UserInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI State (既存完全保持)
  const [userEditorOpen, setUserEditorOpen] = useState(false);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuUserId, setMenuUserId] = useState<string | null>(null);

  // 🆕 Organization State (Issue #285) - Reserved for future implementation

  // Load data on mount
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [usersData, statsData, invitationsData] = await Promise.all([
        userManagementService.getUsers(),
        userManagementService.getUserStats(),
        userManagementService.getInvitations(),
      ]);
      
      setUsers(usersData);
      setStats(statsData);
      setInvitations(invitationsData);
    } catch (err) {
      setError('ユーザーデータの読み込みでエラーが発生しました');
      console.error('Error loading user data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = () => {
    setInvitationDialogOpen(true);
  };

  // 🆕 Organization Management Helpers (Issue #285) - Reserved for future implementation

  const handleEditUser = (user: UserInfo) => {
    setSelectedUser(user);
    setUserEditorOpen(true);
    handleMenuClose();
  };

  const handleDeleteUser = (user: UserInfo) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      const success = await userManagementService.deleteUser(userToDelete.id);
      if (success) {
        await loadUserData();
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
      } else {
        setError('ユーザーの削除に失敗しました');
      }
    } catch (err) {
      setError('ユーザー削除中にエラーが発生しました');
    }
  };

  const handleUserSave = async (userData: Partial<UserInfo>) => {
    try {
      let success = false;
      
      if (selectedUser) {
        // 更新
        success = await userManagementService.updateUser(selectedUser.id, userData);
      } else {
        // 新規作成
        const newUserId = await userManagementService.createUser(userData as Omit<UserInfo, 'id' | 'createdAt' | 'updatedAt'>);
        success = !!newUserId;
      }
      
      if (success) {
        await loadUserData();
        setUserEditorOpen(false);
      } else {
        setError('ユーザーの保存に失敗しました');
      }
    } catch (err) {
      setError('ユーザー保存中にエラーが発生しました');
    }
  };

  const handleSendInvitation = async (email: string, role: UserRole, message?: string) => {
    try {
      const invitationId = await userManagementService.sendInvitation(email, role, message);
      if (invitationId) {
        await loadUserData();
        setInvitationDialogOpen(false);
      } else {
        setError('招待の送信に失敗しました');
      }
    } catch (err) {
      setError('招待送信中にエラーが発生しました');
    }
  };

  const handleMenuOpen = (event: ReactClickEvent, userId: string) => {
    setAnchorEl(event.currentTarget as HTMLElement);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const getRoleChip = (role: UserRole) => {
    const roleConfig = {
      owner: { label: 'オーナー', color: 'primary' as const, icon: <AdminIcon /> },
      manager: { label: 'マネージャー', color: 'secondary' as const, icon: <AdminIcon /> },
      staff: { label: 'スタッフ', color: 'default' as const, icon: <PersonIcon /> },
      receptionist: { label: '受付', color: 'info' as const, icon: <PersonIcon /> },
    };

    const config = roleConfig[role];
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
      />
    );
  };

  const getStatusChip = (status: UserStatus) => {
    const statusConfig = {
      active: { label: 'アクティブ', color: 'success' as const, icon: <CheckCircleIcon /> },
      inactive: { label: '無効', color: 'error' as const, icon: <BlockIcon /> },
      pending: { label: '招待中', color: 'warning' as const, icon: <PendingIcon /> },
    };
    
    const config = statusConfig[status];
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const clearError = () => {
    setError(null);
  };

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rectangular" width={180} height={36} />
      </Box>
      <Grid container spacing={3} mb={4}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={60} height={32} sx={{ mt: 1 }} />
                <Skeleton variant="text" width={80} height={20} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rectangular" height={400} />
    </Box>
  );

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {renderLoadingSkeleton()}
      </motion.div>
    );
  }

  // 🆕 Organization View Rendering (Issue #285) - Using Standard UI for now
  if (organizationView) {
    // Fall back to standard user management UI
    // TODO: Implement OrganizationUserManager component
  }

  // 既存UI（完全保持）
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {/* ヘッダー */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" component="h2" fontWeight="bold">
              ユーザー管理
            </Typography>
            <Typography variant="body2" color="text.secondary">
              スタッフアカウントと権限を管理します
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleAddUser}
            >
              新規ユーザー招待
            </Button>
          </Box>
        </Box>

        {/* 統計カード（シンプル化） */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats?.byStatus.active || 0}
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      アクティブユーザー
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PendingIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats?.byStatus.pending || 0}
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      招待中
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ユーザーテーブル */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ユーザー</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>役割</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>最終ログイン</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        ユーザーが登録されていません
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        新しいユーザーを招待して始めましょう
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={handleAddUser}
                      >
                        新規ユーザー招待
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar 
                            src={user.basicInfo.avatar} 
                            sx={{ width: 36, height: 36, mr: 2, bgcolor: 'primary.main' }}
                          >
                            {user.basicInfo.lastName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.basicInfo.lastName} {user.basicInfo.firstName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {user.basicInfo.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{getRoleChip(user.access.role)}</TableCell>
                      <TableCell>{getStatusChip(user.access.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {user.access.lastLogin ?
                            user.access.lastLogin.toLocaleString('ja-JP') :
                            '未ログイン'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user.id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              const user = users.find(u => u.id === menuUserId);
              if (user) handleEditUser(user);
            }}
          >
            <EditIcon sx={{ mr: 1 }} />
            編集
          </MenuItem>
          {(() => {
            const user = users.find(u => u.id === menuUserId);
            return user?.access.role !== 'owner' && (
              <MenuItem
                onClick={() => {
                  if (user) handleDeleteUser(user);
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ mr: 1 }} />
                削除
              </MenuItem>
            );
          })()}
        </Menu>

        {/* User Editor Dialog */}
        <UserEditorDialog
          open={userEditorOpen}
          user={selectedUser}
          onClose={() => setUserEditorOpen(false)}
          onSave={handleUserSave}
        />

        {/* Invitation Dialog */}
        <InvitationDialog
          open={invitationDialogOpen}
          onClose={() => setInvitationDialogOpen(false)}
          onSend={handleSendInvitation}
          organizationView={organizationView}
          availableStores={organization?.availableStores || []}
          tenantContext={tenantContext}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>ユーザー削除の確認</DialogTitle>
          <DialogContent>
            <Typography>
              {userToDelete && (
                <>
                  「{userToDelete.basicInfo.lastName} {userToDelete.basicInfo.firstName}」を削除しますか？
                  <br />
                  この操作は取り消すことができません。
                </>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              削除
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

// User Editor Dialog Component
const UserEditorDialog: React.FC<UserEditorDialogProps> = ({ open, user, onClose, onSave }) => {
  interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    role: UserRole;
  }

  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'staff' as UserRole,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.basicInfo.firstName,
        lastName: user.basicInfo.lastName,
        email: user.basicInfo.email,
        role: user.access.role,
      });
    }
    setErrors({});
  }, [user, open]);

  const handleInputChange = (field: keyof UserFormData, value: string | UserRole) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field as string]) {
      setErrors({ ...errors, [field as string]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName?.trim()) newErrors.firstName = '名前は必須です';
    if (!formData.lastName?.trim()) newErrors.lastName = '姓は必須です';
    if (!formData.email?.trim()) newErrors.email = 'メールアドレスは必須です';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    if (!user) return; // 編集のみ対応

    const userData: Partial<UserInfo> = {
      basicInfo: {
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        avatar: user.basicInfo.avatar,
      },
      access: {
        role: formData.role || 'staff',
        status: user.access.status, // ステータスは変更しない
        permissions: user.access.permissions,
        joinDate: user.access.joinDate,
        lastLogin: user.access.lastLogin,
      },
      settings: user.settings, // 通知設定は変更しない
      updatedBy: 'current-user',
    };

    onSave(userData);
  };

  if (!user) return null; // 編集専用なのでユーザーがない場合は表示しない

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        ユーザー編集
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="姓"
              value={formData.lastName || ''}
              onChange={(e: ReactChangeEvent) => handleInputChange('lastName', e.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="名前"
              value={formData.firstName || ''}
              onChange={(e: ReactChangeEvent) => handleInputChange('firstName', e.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="メールアドレス"
              type="email"
              value={formData.email || ''}
              onChange={(e: ReactChangeEvent) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>役割</InputLabel>
              <Select
                value={formData.role || 'staff'}
                onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                label="役割"
              >
                <MenuItem value="owner">オーナー</MenuItem>
                <MenuItem value="manager">マネージャー</MenuItem>
                <MenuItem value="staff">スタッフ</MenuItem>
                <MenuItem value="receptionist">受付</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button onClick={handleSave} variant="contained">
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Invitation Dialog Component
const InvitationDialog: React.FC<InvitationDialogProps> = ({
  open,
  onClose,
  onSend,
  organizationView = false,
  availableStores = [],
  tenantContext
}) => {
  // 🆕 Organization View Switch (Issue #285)
  if (organizationView) {
    return (
      <TenantUserInvitation
        open={open}
        onClose={onClose}
        onSend={onSend}
        availableStores={availableStores}
        tenantContext={tenantContext}
      />
    );
  }

  // 既存のInvitation Dialog（完全保持）
  return <StandardInvitationDialog open={open} onClose={onClose} onSend={onSend} />;
};

// 🆕 Tenant User Invitation Component (Issue #285)
interface TenantUserInvitationProps {
  open: boolean;
  onClose: () => void;
  onSend: (email: string, role: UserRole, message?: string) => void;
  availableStores: StoreInfo[];
  tenantContext?: TenantContext;
}

const TenantUserInvitation: React.FC<TenantUserInvitationProps> = ({
  open,
  onClose,
  onSend,
  availableStores,
  tenantContext
}) => {
  interface InviteFormData {
    email: string;
    role: UserRole;
    stores: string[];
    permissions: Record<string, string[]>;
    customMessage: string;
  }

  const [inviteForm, setInviteForm] = useState<InviteFormData>({
    email: '',
    role: 'staff',
    stores: [],
    permissions: {},
    customMessage: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    '基本情報',
    '権限設定',
    '店舗アクセス',
    '招待メッセージ'
  ];

  useEffect(() => {
    if (open) {
      setInviteForm({
        email: '',
        role: 'staff',
        stores: [],
        permissions: {},
        customMessage: ''
      });
      setErrors({});
      setCurrentStep(0);
    }
  }, [open]);

  const handleInputChange = (field: keyof InviteFormData, value: string | UserRole | string[] | Record<string, string[]>) => {
    setInviteForm({ ...inviteForm, [field]: value });
    if (errors[field as string]) {
      setErrors({ ...errors, [field as string]: '' });
    }
  };

  const handlePermissionChange = (resource: string, actions: string[]) => {
    setInviteForm({
      ...inviteForm,
      permissions: {
        ...inviteForm.permissions,
        [resource]: actions
      }
    });
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0: // 基本情報
        if (!inviteForm.email?.trim()) {
          newErrors.email = 'メールアドレスは必須です';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email)) {
          newErrors.email = '有効なメールアドレスを入力してください';
        }
        break;
      case 1: // 権限設定 - 任意
        break;
      case 2: // 店舗アクセス - 任意
        break;
      case 3: // 招待メッセージ - 任意
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(Math.max(currentStep - 1, 0));
  };

  const handleSend = () => {
    if (validateStep(currentStep)) {
      // TODO: Enhanced invitation with permissions and store access
      onSend(inviteForm.email || '', inviteForm.role || 'staff', inviteForm.customMessage || undefined);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle display="flex" alignItems="center">
        <SendIcon sx={{ mr: 1 }} />
        新しいメンバーを招待
      </DialogTitle>

      <DialogContent>
        {/* Stepper */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {steps.map((label, index) => (
              <Box key={index} display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: index <= currentStep ? 'primary.main' : 'grey.300',
                    color: index <= currentStep ? 'white' : 'grey.600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: index <= currentStep ? 'primary.main' : 'grey.600',
                    fontWeight: index === currentStep ? 'bold' : 'normal'
                  }}
                >
                  {label}
                </Typography>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: 40,
                      height: 2,
                      backgroundColor: index < currentStep ? 'primary.main' : 'grey.300',
                      mx: 2
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Step Content */}
        <Box sx={{ minHeight: 300 }}>
          {currentStep === 0 && (
            <RolePermissionSelector
              email={inviteForm.email || ''}
              role={inviteForm.role || 'staff'}
              onEmailChange={(email) => handleInputChange('email', email)}
              onRoleChange={(role) => handleInputChange('role', role)}
              errors={errors}
            />
          )}

          {currentStep === 1 && (
            <AdvancedPermissionSelector
              permissions={inviteForm.permissions || {}}
              role={inviteForm.role || 'staff'}
              onChange={handlePermissionChange}
            />
          )}

          {currentStep === 2 && (
            <StoreAccessSelector
              selectedStores={inviteForm.stores || []}
              availableStores={availableStores}
              onChange={(stores) => handleInputChange('stores', stores)}
            />
          )}

          {currentStep === 3 && (
            <InvitationMessageEditor
              message={inviteForm.customMessage || ''}
              onChange={(message) => handleInputChange('customMessage', message)}
              tenantContext={tenantContext}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        {currentStep > 0 && (
          <Button onClick={handleBack}>戻る</Button>
        )}
        {currentStep < steps.length - 1 ? (
          <Button onClick={handleNext} variant="contained">
            次へ
          </Button>
        ) : (
          <Button onClick={handleSend} variant="contained" startIcon={<SendIcon />}>
            招待送信
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// 🆕 Role Permission Selector Component (Issue #285)
const RolePermissionSelector: React.FC<{
  email: string;
  role: UserRole;
  onEmailChange: (email: string) => void;
  onRoleChange: (role: UserRole) => void;
  errors: Record<string, string>;
}> = ({ email, role, onEmailChange, onRoleChange, errors }) => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>基本情報</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        招待するメンバーの基本情報を入力してください
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="メールアドレス"
        type="email"
        value={email}
        onChange={(e: ReactChangeEvent) => onEmailChange(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        placeholder="招待するユーザーのメールアドレス"
        required
      />
    </Grid>
    <Grid item xs={12}>
      <FormControl fullWidth>
        <InputLabel>役割</InputLabel>
        <Select
          value={role}
          onChange={(e) => onRoleChange(e.target.value as UserRole)}
          label="役割"
        >
          <MenuItem value="owner">オーナー</MenuItem>
          <MenuItem value="manager">マネージャー</MenuItem>
          <MenuItem value="staff">スタッフ</MenuItem>
          <MenuItem value="receptionist">受付</MenuItem>
        </Select>
      </FormControl>
    </Grid>
  </Grid>
);

// 🆕 Advanced Permission Selector Component (Issue #285)
const AdvancedPermissionSelector: React.FC<{
  permissions: Record<string, string[]>;
  role: UserRole;
  onChange: (resource: string, actions: string[]) => void;
}> = ({ permissions, role: _role, onChange }) => {
  const resources = ['customers', 'reservations', 'services', 'staff', 'reports', 'settings', 'billing'];
  const actions = ['read', 'write', 'delete', 'admin'];

  const handlePermissionToggle = (resource: string, action: string, checked: boolean) => {
    const currentActions = permissions[resource] || [];
    const newActions = checked
      ? [...currentActions, action]
      : currentActions.filter(a => a !== action);
    onChange(resource, newActions);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>権限設定</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        招待するメンバーの詳細な権限を設定してください（任意）
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>リソース</TableCell>
              {actions.map(action => (
                <TableCell key={action} align="center">
                  <Typography variant="caption">{action}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map(resource => (
              <TableRow key={resource}>
                <TableCell>
                  <Typography variant="body2">{resource}</Typography>
                </TableCell>
                {actions.map(action => (
                  <TableCell key={action} align="center">
                    <Checkbox
                      size="small"
                      checked={(permissions[resource] || []).includes(action)}
                      onChange={(e) => handlePermissionToggle(resource, action, e.target.checked)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// 🆕 Store Access Selector Component (Issue #285)
const StoreAccessSelector: React.FC<{
  selectedStores: string[];
  availableStores: StoreInfo[];
  onChange: (storeIds: string[]) => void;
}> = ({ selectedStores, availableStores, onChange }) => {
  const handleStoreToggle = (storeId: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedStores, storeId]);
    } else {
      onChange(selectedStores.filter(id => id !== storeId));
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>店舗アクセス設定</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        招待するメンバーがアクセス可能な店舗を選択してください（任意）
      </Typography>

      {availableStores.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          利用可能な店舗情報がありません
        </Typography>
      ) : (
        <List sx={{ mt: 2 }}>
          {availableStores.map(store => (
            <ListItem key={store.id} divider>
              <ListItemIcon>
                <Checkbox
                  checked={selectedStores.includes(store.id)}
                  onChange={(e) => handleStoreToggle(store.id, e.target.checked)}
                />
              </ListItemIcon>
              <ListItemIcon>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText
                primary={store.name}
                secondary={store.address}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

// 🆕 Invitation Message Editor Component (Issue #285)
const InvitationMessageEditor: React.FC<{
  message: string;
  onChange: (message: string) => void;
  tenantContext?: TenantContext;
}> = ({ message, onChange, tenantContext }) => {
  const defaultMessage = `${tenantContext?.name || 'サロンハブ'}にご招待いたします。

アカウント作成後、すぐにチームメンバーとして活動を開始していただけます。

何かご質問がございましたら、お気軽にお声がけください。

よろしくお願いいたします。`;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>招待メッセージ</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        招待メールに含めるカスタムメッセージを入力してください（任意）
      </Typography>

      <TextField
        fullWidth
        label="招待メッセージ"
        multiline
        rows={6}
        value={message}
        onChange={(e: ReactChangeEvent) => onChange(sanitizeInput(e.target.value))}
        placeholder={defaultMessage}
        sx={{ mt: 2 }}
      />

      <Box sx={{ mt: 2 }}>
        <Button
          size="small"
          onClick={() => onChange(defaultMessage)}
          variant="outlined"
        >
          デフォルトメッセージを使用
        </Button>
      </Box>
    </Box>
  );
};

// 🆕 Standard Invitation Dialog (既存機能完全保持)
const StandardInvitationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSend: (email: string, role: UserRole, message?: string) => void;
}> = ({ open, onClose, onSend }) => {
  interface StandardFormData {
    email: string;
    role: UserRole;
    message: string;
  }

  const [formData, setFormData] = useState<StandardFormData>({
    email: '',
    role: 'staff',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setFormData({
        email: '',
        role: 'staff',
        message: '',
      });
      setErrors({});
    }
  }, [open]);

  const handleInputChange = (field: keyof StandardFormData, value: string | UserRole) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field as string]) {
      setErrors({ ...errors, [field as string]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email?.trim()) {
      newErrors.email = 'メールアドレスは必須です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = () => {
    if (!validateForm()) return;
    onSend(formData.email || '', formData.role || 'staff', formData.message || undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle display="flex" alignItems="center">
        <SendIcon sx={{ mr: 1 }} />
        新規ユーザー招待
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="メールアドレス"
              type="email"
              value={formData.email || ''}
              onChange={(e: ReactChangeEvent) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              placeholder="招待するユーザーのメールアドレス"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>役割</InputLabel>
              <Select
                value={formData.role || 'staff'}
                onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                label="役割"
              >
                <MenuItem value="owner">オーナー</MenuItem>
                <MenuItem value="manager">マネージャー</MenuItem>
                <MenuItem value="staff">スタッフ</MenuItem>
                <MenuItem value="receptionist">受付</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="招待メッセージ（任意）"
              multiline
              rows={3}
              value={formData.message || ''}
              onChange={(e: ReactChangeEvent) => handleInputChange('message', sanitizeInput(e.target.value))}
              placeholder="新しいメンバーへのメッセージを入力してください..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button onClick={handleSend} variant="contained" startIcon={<SendIcon />}>
          招待送信
        </Button>
      </DialogActions>
    </Dialog>
  );
};


export default memo(UserManagementTab);